import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CenterFocusStrongRoundedIcon from "@mui/icons-material/CenterFocusStrongRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import {
  alpha,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { lighten } from "@mui/material/styles";
import I18n from "i18n";
import {
  PointerEvent as ReactPointerEvent,
  WheelEvent as ReactWheelEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SelfSeatingLayout, SelfSeatingLayoutObject } from "./selfSeatingApi";

interface SelfSeatingMapProps {
  layout: SelfSeatingLayout;
  selectedIds: Set<string>;
  onToggle: (table: SelfSeatingLayoutObject) => void;
  canSelect: (table: SelfSeatingLayoutObject) => boolean;
}

interface ViewState {
  scale: number;
  x: number;
  y: number;
}

const MIN_SCALE = 0.45;
const MAX_SCALE = 2.4;

export default function SelfSeatingMap({
  layout,
  selectedIds,
  onToggle,
  canSelect,
}: SelfSeatingMapProps) {
  const theme = useTheme();
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ pointerId: number; x: number; y: number }>();
  const [view, setView] = useState<ViewState>({ scale: 1, x: 0, y: 0 });
  const bounds = useMemo(() => {
    const width = Math.max(
      360,
      ...layout.layout.map(item => item.x + item.width + 24),
    );
    const height = Math.max(
      320,
      ...layout.layout.map(item => item.y + item.height + 24),
    );
    return { width, height };
  }, [layout]);

  const fit = () => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }
    const padding = 24;
    const scale = Math.min(
      1,
      (viewport.clientWidth - padding * 2) / bounds.width,
      (viewport.clientHeight - padding * 2) / bounds.height,
    );
    setView({
      scale,
      x: Math.max(padding, (viewport.clientWidth - bounds.width * scale) / 2),
      y: Math.max(padding, (viewport.clientHeight - bounds.height * scale) / 2),
    });
  };

  useEffect(() => {
    fit();
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(fit);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [bounds.width, bounds.height]);

  const zoom = (factor: number) => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }
    setView(current => {
      const scale = Math.min(
        MAX_SCALE,
        Math.max(MIN_SCALE, current.scale * factor),
      );
      const centerX = viewport.clientWidth / 2;
      const centerY = viewport.clientHeight / 2;
      return {
        scale,
        x: centerX - ((centerX - current.x) / current.scale) * scale,
        y: centerY - ((centerY - current.y) / current.scale) * scale,
      };
    });
  };

  const handleWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    zoom(event.deltaY < 0 ? 1.12 : 0.89);
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("button")) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    const deltaX = event.clientX - drag.x;
    const deltaY = event.clientY - drag.y;
    dragRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
    setView(current => ({
      ...current,
      x: current.x + deltaX,
      y: current.y + deltaY,
    }));
  };

  const stopDragging = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = undefined;
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <Box
      ref={viewportRef}
      onWheel={handleWheel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: "52vh", sm: 520, lg: 580 },
        minHeight: 380,
        overflow: "hidden",
        touchAction: "none",
        cursor: dragRef.current ? "grabbing" : "grab",
        backgroundColor: "#eef2f1",
        backgroundImage: `linear-gradient(${alpha(theme.palette.primary.dark, 0.05)} 1px, transparent 1px), linear-gradient(90deg, ${alpha(theme.palette.primary.dark, 0.05)} 1px, transparent 1px)`,
        backgroundSize: "20px 20px",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: bounds.width,
          height: bounds.height,
          transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})`,
          transformOrigin: "0 0",
        }}
      >
        {layout.layout.map(item => {
          const isSelectable = item.type === "table" || item.type === "seat";
          if (!isSelectable) {
            return (
              <Box
                key={item.id}
                aria-hidden
                sx={{
                  position: "absolute",
                  left: item.x,
                  top: item.y,
                  width: item.width,
                  height: item.height,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `2px solid ${item.color || alpha(theme.palette.text.primary, 0.35)}`,
                  backgroundColor: item.color
                    ? alpha(item.color, 0.16)
                    : alpha(theme.palette.common.white, 0.7),
                  color: "text.secondary",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <Typography variant="caption" fontWeight={700} noWrap>
                  {I18n.locale === "en" && item.name_en
                    ? item.name_en
                    : item.name}
                </Typography>
              </Box>
            );
          }

          const selected = selectedIds.has(item.id);
          const unavailable =
            !selected && (item.occupied || item.available === false);
          const disabled = unavailable || (!selected && !canSelect(item));
          const stateLabel = selected
            ? I18n.t("SelfSeating.tableSelected")
            : unavailable
              ? I18n.t("SelfSeating.tableOccupied")
              : I18n.t("SelfSeating.tableAvailable");
          const tableAriaLabel: string = `${I18n.t("SelfSeating.tableLabel", {
            number:
              I18n.locale === "en" && item.name_en ? item.name_en : item.name,
            capacity: item.max_person || 0,
            state: stateLabel,
          })}`;
          const tableSize = Math.max(36, Math.min(item.width, item.height));
          const tableColor = selected
            ? "#00796b"
            : unavailable
              ? "#d5d9d7"
              : item.color || "#ffffff";
          const stripeColor = selected
            ? lighten("#00796b", 0.18)
            : unavailable
              ? lighten("#d5d9d7", 0.12)
              : lighten(item.color || "#ffffff", 0.12);

          return (
            <Box
              key={item.id}
              sx={{
                position: "absolute",
                left: item.x + item.width / 2,
                top: item.y + item.height / 2,
                width: Math.max(44, item.width),
                height: Math.max(44, item.height),
                transform: "translate(-50%, -50%)",
              }}
            >
              <Box
                component="button"
                type="button"
                disabled={disabled}
                onClick={() => onToggle(item)}
                aria-pressed={selected}
                aria-label={tableAriaLabel}
                sx={{
                  position: "absolute",
                  inset: 0,
                  m: "auto",
                  width: tableSize,
                  height: tableSize,
                  p: 0.5,
                  borderRadius: item.type === "seat" ? "50%" : 1,
                  border: "2px solid",
                  borderColor: selected
                    ? "#00695c"
                    : unavailable
                      ? "#7d8582"
                      : "#357b67",
                  color: theme.palette.getContrastText(tableColor),
                  backgroundColor: tableColor,
                  backgroundImage: item.window
                    ? `repeating-linear-gradient(135deg, ${tableColor} 0, ${tableColor} 7px, ${lighten(stripeColor, 0.18)} 7px, ${lighten(stripeColor, 0.18)} 14px)`
                    : "none",
                  boxShadow: selected
                    ? `0 0 0 4px ${alpha("#00796b", 0.2)}`
                    : "0 2px 6px rgba(25, 54, 46, 0.16)",
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: unavailable ? 0.78 : 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "inherit",
                  "&:focus-visible": {
                    outline: "3px solid #f2a900",
                    outlineOffset: 3,
                  },
                }}
              >
                <Typography
                  component="span"
                  fontSize={12}
                  lineHeight={1}
                  fontWeight={800}
                >
                  {selected ? "✓ " : unavailable ? "× " : ""}
                  {I18n.locale === "en" && item.name_en
                    ? item.name_en
                    : item.name}
                </Typography>
                <Typography
                  component="span"
                  fontSize={9}
                  lineHeight={1.3}
                  fontWeight={700}
                >
                  {item.max_person || 0} {I18n.t("SelfSeating.personShort")}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Stack
        direction="column"
        spacing={0.5}
        sx={{ position: "absolute", right: 12, top: 12 }}
      >
        <Tooltip title={I18n.t("SelfSeating.zoomIn")} placement="left">
          <IconButton
            onClick={() => zoom(1.18)}
            sx={controlSx}
            aria-label={I18n.t("SelfSeating.zoomIn")}
          >
            <AddRoundedIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={I18n.t("SelfSeating.zoomOut")} placement="left">
          <IconButton
            onClick={() => zoom(0.84)}
            sx={controlSx}
            aria-label={I18n.t("SelfSeating.zoomOut")}
          >
            <RemoveRoundedIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={I18n.t("SelfSeating.fitMap")} placement="left">
          <IconButton
            onClick={fit}
            sx={controlSx}
            aria-label={I18n.t("SelfSeating.fitMap")}
          >
            <CenterFocusStrongRoundedIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

const controlSx = {
  width: 44,
  height: 44,
  color: "#173c33",
  backgroundColor: "rgba(255,255,255,.94)",
  border: "1px solid rgba(23,60,51,.16)",
  boxShadow: "0 3px 12px rgba(23,60,51,.12)",
  "&:hover": { backgroundColor: "#fff" },
};
